import {
  Button,
  FormField,
  FormGroup,
  FormLabel,
  Modal,
  ModalBody,
  ModalDialog,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@ax/ui';
import { useEffect, useRef, useState } from 'react';
import style from './NoteTemplates.module.scss';
import { Controller, FieldError, SubmitHandler, useForm } from 'react-hook-form';
import { NOTE_SECTION } from '@/constants/note-section';
import {
  useAppDispatch,
  createTemplateRequested,
  useAppSelector,
  updateTemplateRequested,
  deleteTemplateRequested,
  newTemplateDiscarded,
  templatediscardCancel,
  templatediscardConfirmed,
  templateFormEdited,
  templateFormCleaned,
  Template,
} from '@ax/redux-store';
import useAnalytics from '@/hooks/useAnalytics';
import { NEW_TEMPLATE_TEMP_ID } from '@ax/constants';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { ClassicEditor, Paragraph, Essentials, Undo } from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import SelectField from '@ax/ui/src/components/SelectField';
import { TEMPLATE_GENDER_CONSTANTS } from '@/constants/template-gender-constants';

const NoteTemplates = () => {
  const analytics = useAnalytics();
  const MODAL_TITLE_DELETE = 'Please confirm';
  const MODAL_BODY_TEXT_DELETE = 'Do you want to Delete this template?';
  const MODAL_BTN_TEXT_DELETE = 'Delete';

  const MODAL_TITLE_REVERT = 'Please confirm';
  const MODAL_BODY_TEXT_REVERT = 'You have unsaved changes. Do you want to discard them?';
  const MODAL_BTN_TEXT_REVERT = 'Yes';

  const dispatch = useAppDispatch();
  const formValues = useAppSelector((state) => state.templates.current);
  const { show: isDiscardConfirmationShow, postDiscardActions } = useAppSelector(
    (state) => state.templates.discardConfirmation,
  );
  const [modal, setModal] = useState(false);
  const [modalTitle, setModalTitle] = useState(MODAL_TITLE_DELETE);
  const [modalBodyText, setModalBodyText] = useState(MODAL_BODY_TEXT_DELETE);
  const [modalBtnName, setModalBtnName] = useState(MODAL_BTN_TEXT_DELETE);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isReverting, setIsReverting] = useState(false);
  const isCKEditorFirstChange = useRef(true);

  const {
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors, isDirty },
  } = useForm<Template>({ defaultValues: formValues });

  const isNew = formValues?.id === NEW_TEMPLATE_TEMP_ID;

  const EMPTY_SELECT_VALUE = { value: '', label: 'Select...' };

  const genderOptions = Object.keys(TEMPLATE_GENDER_CONSTANTS).map((key) => ({
    value: TEMPLATE_GENDER_CONSTANTS[key].label,
    label: TEMPLATE_GENDER_CONSTANTS[key].label,
  }));

  genderOptions.unshift(EMPTY_SELECT_VALUE);

  const noteSectionOptions = Object.values(NOTE_SECTION)
    .filter((section) => section === NOTE_SECTION.pe || section === NOTE_SECTION.ros)
    .map((section) => ({
      value: section,
      label: section,
    }));

  noteSectionOptions.unshift(EMPTY_SELECT_VALUE);

  const onSubmit: SubmitHandler<Template> = (data) => {
    const analyticsProperties = {
      eventDateTime: new Date()?.toString(),
      templateName: formValues.title,
    };

    if (data.id === NEW_TEMPLATE_TEMP_ID) {
      data.id = '';
      data.name = data.title;
      data.content = data.content?.replace(/(<br\s*\/?>|<\/p>)(?!\n)(?=.*\S)/g, '$1\n');
      dispatch(createTemplateRequested({ template: data }));
      analytics.onSaveTemplate(analyticsProperties);
    } else {
      data.content = data.content?.replace(/(<br\s*\/?>|<\/p>)(?!\n)(?=.*\S)/g, '$1\n');
      dispatch(updateTemplateRequested({ template: data }));
      analytics.onUpdateTemplate(analyticsProperties);
    }
  };

  const handleCancelClick = () => {
    if (isDiscardConfirmationShow) {
      dispatch(templatediscardCancel({}));
    }

    if (isDeleting) {
      const analyticsProperties = {
        eventDateTime: new Date()?.toString(),
        templateName: formValues.title,
        userConfirmation: 'No',
      };
      analytics.onDeleteTemplateConfirm(analyticsProperties);
    }

    if (isReverting) {
      const analyticsProperties = {
        eventDateTime: new Date()?.toString(),
        templateName: formValues.title,
        userConfirmation: 'No',
      };
      analytics.onClearTemplateConfirm(analyticsProperties);
    }

    setIsDeleting(false);
    setIsReverting(false);
    setModal(false);
  };

  const handleActionClick = () => {
    if (isDeleting && isDiscardConfirmationShow === false) {
      dispatch(deleteTemplateRequested({ templateId: formValues.id }));
      setIsDeleting(false);
      const analyticsProperties = {
        eventDateTime: new Date()?.toString(),
        templateName: formValues.title,
        userConfirmation: 'Yes',
      };
      analytics.onDeleteTemplateConfirm(analyticsProperties);
    }

    if (
      isReverting &&
      formValues.id != NEW_TEMPLATE_TEMP_ID &&
      isDiscardConfirmationShow === false
    ) {
      reset(formValues);
      setIsReverting(false);
    }

    if (
      isReverting &&
      formValues.id === NEW_TEMPLATE_TEMP_ID &&
      isDiscardConfirmationShow === false
    ) {
      dispatch(newTemplateDiscarded({}));
      setIsReverting(false);
    }

    if (isReverting && isDiscardConfirmationShow) {
      dispatch(templatediscardConfirmed({ actions: postDiscardActions }));
      setIsReverting(false);
    }

    if (isReverting) {
      const analyticsProperties = {
        eventDateTime: new Date()?.toString(),
        templateName: formValues.title,
        userConfirmation: 'Yes',
      };
      analytics.onClearTemplateConfirm(analyticsProperties);
    }

    setModal(false);
  };

  useEffect(() => {
    if (formValues) {
      reset(formValues);
      isCKEditorFirstChange.current = true;
    }
  }, [reset, formValues]);

  useEffect(() => {
    if (isDirty) {
      dispatch(templateFormEdited({ templateId: formValues?.id, isDirty }));
    } else {
      dispatch(templateFormCleaned({ templateId: formValues?.id, isDirty }));
    }
  }, [isDirty]);

  useEffect(() => {
    if (isDiscardConfirmationShow) {
      setDiscardConfirmation();
    }
  }, [isDiscardConfirmationShow]);

  const setDiscardConfirmation = () => {
    setModalTitle(MODAL_TITLE_REVERT);
    setModalBodyText(MODAL_BODY_TEXT_REVERT);
    setModalBtnName(MODAL_BTN_TEXT_REVERT);
    setIsDeleting(false);
    setIsReverting(true);
    setModal(true);
    const analyticsProperties = {
      eventDateTime: new Date()?.toString(),
      templateName: formValues.title,
    };
    analytics.onClearTemplate(analyticsProperties);
  };

  const setDeletingModal = () => {
    setModalTitle(MODAL_TITLE_DELETE);
    setModalBodyText(MODAL_BODY_TEXT_DELETE);
    setModalBtnName(MODAL_BTN_TEXT_DELETE);
    setIsDeleting(true);
    setIsReverting(false);
    setModal(true);
    const analyticsProperties = {
      eventDateTime: new Date()?.toString(),
      templateName: formValues.title,
    };
    analytics.onDeleteTemplate(analyticsProperties);
  };

  const memoize = (fn: (v: string) => void) => {
    let lastArgument = '';

    return (argument: string, updateArgumentOnly = false) => {
      if (updateArgumentOnly) {
        lastArgument = argument;
        return;
      }

      if (lastArgument !== argument) {
        lastArgument = argument;
        return fn(argument);
      }
    };
  };

  return (
    <>
      {formValues && (
        <div className={style.templates_page}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className={style.templates_form_container}>
              <div className={style.templates_form_container__left}>
                <FormGroup className="mb-[15px]">
                  <FormLabel className="input__label">Title</FormLabel>
                  <FormField error={errors.title as FieldError}>
                    <Controller
                      rules={{
                        validate: (value) => {
                          if (!value) {
                            return 'Please input a title';
                          }

                          return true;
                        },
                      }}
                      name={'title'}
                      control={control}
                      render={({ field }) => (
                        <input
                          type="text"
                          className="input"
                          {...field}
                        />
                      )}
                    />
                  </FormField>
                </FormGroup>
                <FormGroup className="mb-[15px]">
                  <FormLabel className="input__label">Voice Trigger</FormLabel>
                  <FormLabel className="input__label--note">
                    Saying this phrase will automatically select this template
                  </FormLabel>
                  <FormField error={errors.voiceTrigger as FieldError}>
                    <Controller
                      rules={{
                        validate: (value) => {
                          if (!value) {
                            return 'Please input a voice trigger';
                          }

                          return true;
                        },
                      }}
                      name={'voiceTrigger'}
                      control={control}
                      render={({ field }) => (
                        <input
                          type="text"
                          className="input"
                          {...field}
                        />
                      )}
                    />
                  </FormField>
                </FormGroup>
                <FormGroup className="mb-[15px]">
                  <FormLabel className="input__label">Note Section</FormLabel>
                  <Controller
                    rules={{
                      validate: (value) => {
                        if (!value || value === '--') {
                          return 'Please select a note section';
                        }

                        return true;
                      },
                    }}
                    name={'noteSection'}
                    control={control}
                    render={({ field }) => (
                      <SelectField
                        {...field}
                        control={control}
                        defaultValue={formValues.noteSection}
                        name="noteSection"
                        options={noteSectionOptions}
                        value={noteSectionOptions.find(
                          (option) => option.value === watch('noteSection'),
                        )}
                      />
                    )}
                  />
                  {errors.noteSection && (
                    <div className={style.templates_form_container__field_error_message}>
                      {errors.noteSection.message}
                    </div>
                  )}
                </FormGroup>
                <FormGroup className="mb-[15px]">
                  <FormLabel className="input__label">Age</FormLabel>
                  <FormLabel className="input__label--note">
                    Age will be used to help filter down templates. It can be a range or a specific
                    age.
                  </FormLabel>
                  <FormField error={errors.age as FieldError}>
                    <Controller
                      rules={{
                        validate: (value) => {
                          if (!value || value === '--') {
                            return 'Please input an age';
                          }

                          return true;
                        },
                      }}
                      name={'age'}
                      control={control}
                      render={({ field }) => (
                        <input
                          type="text"
                          className="input"
                          {...field}
                        />
                      )}
                    />
                  </FormField>
                </FormGroup>
                <FormGroup className="mb-[15px]">
                  <FormLabel className="input__label">Gender</FormLabel>
                  <Controller
                    rules={{
                      validate: (value) => {
                        if (!value || value === '--') {
                          return 'Please select a gender';
                        }

                        return true;
                      },
                    }}
                    name={'gender'}
                    control={control}
                    render={({ field }) => (
                      <SelectField
                        {...field}
                        defaultValue={formValues.gender}
                        control={control}
                        name="gender"
                        options={genderOptions}
                        value={genderOptions.find((option) => option.value === watch('gender'))}
                      />
                    )}
                  />
                  {errors.gender && (
                    <div className={style.templates_form_container__field_error_message}>
                      {errors.gender.message}
                    </div>
                  )}
                </FormGroup>
              </div>
              <div className={style.templates_form_container__left_bottom}></div>
              <div className={style.templates_form_container__right}>
                <Controller
                  name={'content'}
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => {
                    const memoizedFieldOnChange = memoize((value: string) => {
                      field.onChange(value);
                    });
                    return (
                      <CKEditor
                        editor={ClassicEditor}
                        data={field.value}
                        onChange={(_: any, editor: ClassicEditor) => {
                          const data = editor.getData();

                          if (isCKEditorFirstChange.current) {
                            isCKEditorFirstChange.current = false;
                            return memoizedFieldOnChange(data, true);
                          }

                          memoizedFieldOnChange(data);
                        }}
                        config={{
                          toolbar: {
                            items: ['undo', 'redo'],
                          },
                          plugins: [Undo, Paragraph, Essentials],
                        }}
                      />
                    );
                  }}
                />
                {errors.content && (
                  <div className={style.templates_form_container__field_error_message}>
                    {errors.content.message || 'Template content is required'}
                  </div>
                )}
              </div>
              <div className={style.templates_form_container__actions}>
                <div>
                  <Button
                    className="h-[40px]"
                    block
                    type="button"
                    variant="delete"
                    onClick={setDeletingModal}
                    disabled={formValues.id == NEW_TEMPLATE_TEMP_ID}
                  >
                    Delete
                  </Button>
                </div>
                <div className={style.templates_form_container__actions__right}>
                  <Button
                    className="h-[40px]"
                    block
                    variant="cancel"
                    type="reset"
                    onClick={setDiscardConfirmation}
                    disabled={!(isDirty || formValues.id === NEW_TEMPLATE_TEMP_ID)}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="h-[40px]"
                    block
                    variant="primary"
                    type="submit"
                    disabled={isNew ? false : !isDirty}
                  >
                    {formValues.id === NEW_TEMPLATE_TEMP_ID ? 'Save' : 'Update'}
                  </Button>
                </div>
              </div>
            </div>
          </form>

          <Modal onShow={isDiscardConfirmationShow || modal}>
            <ModalDialog>
              <ModalHeader onHide={handleCancelClick}>
                <ModalTitle>{modalTitle}</ModalTitle>
              </ModalHeader>
              <ModalBody>
                <div className="flex flex-col gap-y-1">
                  <p style={{ textAlign: 'center' }}>{modalBodyText}</p>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button
                  block
                  variant="outline"
                  onClick={handleCancelClick}
                >
                  Cancel
                </Button>
                <Button
                  block
                  variant={isDeleting ? 'delete' : 'primary'}
                  onClick={handleActionClick}
                  type="button"
                >
                  {modalBtnName}
                </Button>
              </ModalFooter>
            </ModalDialog>
          </Modal>
        </div>
      )}

      {formValues ? (
        ''
      ) : (
        <p className={style.content__placeholder}>
          <span className={style.content__placeholder__title}>Templates</span>
          <span className={style.content__placeholder__text}>
            Click on Add new button in order to start.
          </span>
        </p>
      )}
    </>
  );
};

export default NoteTemplates;
